/**
 * TrameQR.jsx — QR code « après cours » sur une fiche compétence.
 *
 * Génère un QR pointant vers la trame filtrée sur la compétence courante :
 *   /formation-125-progression?ex=<exerciseId>&mode=<student|teacher>&phase=<phaseId>
 *
 * Comportement : au scan, l'élève arrive directement sur la trame, dans
 * la bonne phase, sur la bonne compétence, en mode élève par défaut.
 *
 * L'affichage est compact et non intrusif — masqué à l'impression pour
 * ne pas gêner le rendu papier.
 */
import React, { useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, Copy, Check, ScanLine } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Construit l'URL cible pour le QR + le bouton « Copier le lien ».
 * On préfère une URL absolue basée sur `window.location.origin` pour que
 * le QR fonctionne quel que soit l'environnement (preview / prod).
 */
function buildDeepLink({ exId, mode = 'student', phaseId }) {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('ex', exId);
  url.searchParams.set('mode', mode);
  if (phaseId) url.searchParams.set('phase', phaseId);
  return url.toString();
}

export default function TrameQR({
  exId,
  phaseId,
  mode = 'student',
  color = '#e11d48',
  label = 'Scan après cours',
  compact = false,
}) {
  const deepLink = useMemo(() => buildDeepLink({ exId, mode, phaseId }), [exId, mode, phaseId]);
  const [copied, setCopied] = useState(false);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(deepLink);
      setCopied(true);
      toast.success('Lien copié — colle-le dans un message ou un mail à ton élève.');
      setTimeout(() => setCopied(false), 2200);
    } catch (_) {
      toast.error('Copie impossible. Long-press sur le QR pour partager.');
    }
  };

  if (!exId) return null;

  return (
    <div
      className="print:hidden rounded-xl border border-white/10 bg-black/40 p-3 flex items-center gap-3"
      data-testid={`trame-qr-${exId}`}
    >
      <div
        className="bg-white p-1.5 rounded-lg flex-shrink-0"
        style={{ boxShadow: `0 0 0 1px ${color}55` }}
      >
        <QRCodeSVG
          value={deepLink}
          size={compact ? 68 : 84}
          level="M"
          bgColor="#ffffff"
          fgColor="#0a0a0b"
          data-testid={`qr-svg-${exId}`}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest mb-1" style={{ color }}>
          <QrCode className="w-3 h-3" /> {label}
        </div>
        <p className="text-white/75 text-[11.5px] leading-snug mb-2">
          Scanne ce QR pour rouvrir cette compétence directement sur ton téléphone.
        </p>
        <button
          type="button"
          onClick={copyLink}
          className="inline-flex items-center gap-1 h-7 px-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/85 text-[10.5px] font-black uppercase tracking-widest transition"
          data-testid={`qr-copy-${exId}`}
          aria-label="Copier le lien de la compétence"
        >
          {copied ? <Check className="w-3 h-3 text-emerald-300" /> : <Copy className="w-3 h-3" />}
          {copied ? 'Copié' : 'Copier le lien'}
        </button>
      </div>
    </div>
  );
}

/**
 * Variante inline plus discrète — bouton toggle qui déplie le QR à la demande.
 * Utile en mode élève pour éviter d'alourdir chaque carte compétence.
 */
export function TrameQRToggle({ exId, phaseId, mode = 'student', color = '#e11d48' }) {
  const [open, setOpen] = useState(false);
  if (!exId) return null;
  return (
    <div className="print:hidden">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className="inline-flex items-center gap-1 h-7 px-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/85 text-[10.5px] font-black uppercase tracking-widest transition"
        data-testid={`qr-toggle-${exId}`}
        aria-expanded={open}
      >
        <ScanLine className="w-3 h-3" style={{ color }} />
        {open ? 'Masquer QR' : 'QR après cours'}
      </button>
      {open && (
        <div className="mt-2">
          <TrameQR exId={exId} phaseId={phaseId} mode={mode} color={color} compact />
        </div>
      )}
    </div>
  );
}
